/**
 * Pagination Component
 *
 * A responsive pagination control with page numbers, prev/next buttons,
 * and keyboard navigation support.
 *
 * @see types/accounting.ts for PaginationMeta type
 */

import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface PaginationProps {
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Number of page buttons to show on each side of current page */
  siblingCount?: number;
  /** Whether to show first/last page buttons */
  showFirstLast?: boolean;
  /** Whether pagination is disabled */
  disabled?: boolean;
}

/**
 * Generate an array of page numbers to display.
 * Shows ellipsis (...) when there are many pages.
 */
function generatePagination(
  currentPage: number,
  totalPages: number,
  siblingCount: number
): (number | 'ellipsis')[] {
  // Always show first and last page
  const firstPage = 1;
  const lastPage = totalPages;

  // Calculate the range around the current page
  const leftSiblingIndex = Math.max(currentPage - siblingCount, firstPage);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, lastPage);

  // Determine if we need ellipsis
  const showLeftEllipsis = leftSiblingIndex > firstPage + 1;
  const showRightEllipsis = rightSiblingIndex < lastPage - 1;

  const pages: (number | 'ellipsis')[] = [];

  // Always add first page
  if (totalPages > 0) {
    pages.push(firstPage);
  }

  // Add left ellipsis or pages
  if (showLeftEllipsis) {
    pages.push('ellipsis');
  } else {
    // Add pages between first and left sibling
    for (let i = firstPage + 1; i < leftSiblingIndex; i++) {
      pages.push(i);
    }
  }

  // Add pages in the sibling range (excluding first and last which are always shown)
  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    if (i !== firstPage && i !== lastPage) {
      pages.push(i);
    }
  }

  // Add right ellipsis or pages
  if (showRightEllipsis) {
    pages.push('ellipsis');
  } else {
    // Add pages between right sibling and last
    for (let i = rightSiblingIndex + 1; i < lastPage; i++) {
      pages.push(i);
    }
  }

  // Always add last page (if different from first)
  if (totalPages > 1) {
    pages.push(lastPage);
  }

  return pages;
}

/**
 * Pagination renders a pagination control with page numbers and navigation buttons.
 *
 * Features:
 * - Previous/Next buttons
 * - First/Last buttons (optional)
 * - Page number buttons with ellipsis for large page counts
 * - Keyboard navigation (Enter/Space to activate)
 * - Disabled state
 * - Responsive design
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={1}
 *   totalPages={10}
 *   onPageChange={handlePageChange}
 *   siblingCount={1}
 *   showFirstLast
 * />
 * ```
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  showFirstLast = false,
  disabled = false,
}: PaginationProps) {
  const pages = useMemo(
    () => generatePagination(currentPage, totalPages, siblingCount),
    [currentPage, totalPages, siblingCount]
  );

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const handlePageClick = useCallback(
    (page: number) => {
      if (!disabled && page !== currentPage && page >= 1 && page <= totalPages) {
        onPageChange(page);
      }
    },
    [disabled, currentPage, totalPages, onPageChange]
  );

  const handlePrevious = useCallback(() => {
    if (!disabled && canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  }, [disabled, canGoPrevious, currentPage, onPageChange]);

  const handleNext = useCallback(() => {
    if (!disabled && canGoNext) {
      onPageChange(currentPage + 1);
    }
  }, [disabled, canGoNext, currentPage, onPageChange]);

  const handleFirst = useCallback(() => {
    if (!disabled && currentPage !== 1) {
      onPageChange(1);
    }
  }, [disabled, currentPage, onPageChange]);

  const handleLast = useCallback(() => {
    if (!disabled && currentPage !== totalPages) {
      onPageChange(totalPages);
    }
  }, [disabled, currentPage, totalPages, onPageChange]);

  // Don't render if there's only one page or no pages
  if (totalPages <= 1) {
    return null;
  }

  return (
    <nav
      className="flex items-center gap-1"
      aria-label="Pagination"
      data-testid="pagination"
    >
      {/* First page button */}
      {showFirstLast && (
        <button
          type="button"
          onClick={handleFirst}
          disabled={disabled || !canGoPrevious}
          className={clsx(
            'p-2 rounded-md text-sm',
            'transition-colors duration-150',
            disabled || !canGoPrevious
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
            'focus:outline-none focus:ring-2 focus:ring-blue-500'
          )}
          aria-label="Go to first page"
          data-testid="pagination-first"
        >
          <ChevronsLeft className="w-4 h-4" aria-hidden="true" />
        </button>
      )}

      {/* Previous button */}
      <button
        type="button"
        onClick={handlePrevious}
        disabled={disabled || !canGoPrevious}
        className={clsx(
          'p-2 rounded-md text-sm',
          'transition-colors duration-150',
          disabled || !canGoPrevious
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
          'focus:outline-none focus:ring-2 focus:ring-blue-500'
        )}
        aria-label="Go to previous page"
        data-testid="pagination-previous"
      >
        <ChevronLeft className="w-4 h-4" aria-hidden="true" />
      </button>

      {/* Page numbers */}
      <div className="flex items-center gap-1">
        {pages.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-2 py-1 text-sm text-gray-400"
                aria-hidden="true"
              >
                ...
              </span>
            );
          }

          const isCurrentPage = page === currentPage;
          return (
            <button
              key={page}
              type="button"
              onClick={() => handlePageClick(page)}
              disabled={disabled || isCurrentPage}
              className={clsx(
                'min-w-[2rem] px-2 py-1 rounded-md text-sm font-medium',
                'transition-colors duration-150',
                isCurrentPage
                  ? 'bg-blue-600 text-white cursor-default'
                  : disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-100',
                'focus:outline-none focus:ring-2 focus:ring-blue-500'
              )}
              aria-label={`Go to page ${page}`}
              aria-current={isCurrentPage ? 'page' : undefined}
              data-testid={`pagination-page-${page}`}
            >
              {page}
            </button>
          );
        })}
      </div>

      {/* Next button */}
      <button
        type="button"
        onClick={handleNext}
        disabled={disabled || !canGoNext}
        className={clsx(
          'p-2 rounded-md text-sm',
          'transition-colors duration-150',
          disabled || !canGoNext
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
          'focus:outline-none focus:ring-2 focus:ring-blue-500'
        )}
        aria-label="Go to next page"
        data-testid="pagination-next"
      >
        <ChevronRight className="w-4 h-4" aria-hidden="true" />
      </button>

      {/* Last page button */}
      {showFirstLast && (
        <button
          type="button"
          onClick={handleLast}
          disabled={disabled || !canGoNext}
          className={clsx(
            'p-2 rounded-md text-sm',
            'transition-colors duration-150',
            disabled || !canGoNext
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
            'focus:outline-none focus:ring-2 focus:ring-blue-500'
          )}
          aria-label="Go to last page"
          data-testid="pagination-last"
        >
          <ChevronsRight className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
    </nav>
  );
}

export default Pagination;
